// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/mission-launch/ConnectorCatalog.jsx

import React, { useMemo, useState } from 'react';

const ALL_CATEGORY = 'All';

function normalize(value) {
  return String(value || '').trim().toLowerCase();
}

function lifecycleState(connector = {}) {
  return normalize(connector.lifecycle_state || connector.connection_state || 'not_configured');
}

function isOperational(connector = {}) {
  return ['healthy', 'connected'].includes(lifecycleState(connector));
}

function statusLabel(connector = {}) {
  if (connector.status_label) return connector.status_label;

  const state = lifecycleState(connector);
  if (state === 'healthy') return 'Healthy';
  if (state === 'connected') return 'Connected';
  if (state === 'setup_in_progress') return 'Setup in progress';
  if (state === 'reauthorization_required') return 'Reauthorization required';
  if (state === 'degraded' || state === 'needs_attention') return 'Needs attention';
  if (state === 'disconnected') return 'Disconnected';
  if (state === 'paused') return 'Paused';
  return 'Not connected';
}

function statusTone(connector = {}) {
  const state = lifecycleState(connector);
  if (state === 'healthy' || state === 'connected') return 'connected';
  if (state === 'setup_in_progress') return 'progress';
  if (state === 'reauthorization_required' || state === 'degraded' || state === 'needs_attention') {
    return 'attention';
  }
  if (state === 'disconnected') return 'disconnected';
  if (state === 'paused') return 'paused';
  return 'authorization';
}

function connectorCategory(connector = {}) {
  const explicit = String(
    connector.category ||
    connector.provider_category ||
    connector.catalog_category ||
    ''
  ).trim();

  if (explicit) return explicit;

  const key = normalize(connector.key || connector.provider).replace(/-/g, '_');
  if (key === 'aws' || key.includes('azure') || key.includes('gcp') || key.includes('google_cloud')) {
    return 'Cloud';
  }
  if (key === 'okta' || key.includes('entra') || key.includes('identity')) return 'Identity';
  if (key === 'github' || key.includes('gitlab') || key.includes('bitbucket')) return 'DevOps';
  if (key === 'google_workspace' || key.includes('microsoft_365') || key.includes('slack')) {
    return 'Collaboration';
  }
  if (key === 'cloudflare' || key.includes('security') || key.includes('network')) return 'Security';
  return 'Other';
}

function connectorName(connector = {}) {
  return String(
    connector.name ||
    connector.label ||
    connector.provider_name ||
    connector.key ||
    'Connector'
  ).trim();
}

function connectorSubtitle(connector = {}) {
  return String(
    connector.mission_role ||
    connector.headline ||
    connector.description ||
    connector.why_it_matters ||
    'Read-only evidence collection'
  ).trim();
}

function connectorSearchText(connector = {}) {
  return [
    connectorName(connector),
    connectorCategory(connector),
    connectorSubtitle(connector),
    connector.scope,
    connector.coverage,
    connector.provider,
    connector.key,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function ConnectorRow({
  connector,
  recommended = false,
  onToggle,
  onAuthorize,
  compact = false,
}) {
  const selected = connector.selected === true;
  const operational = isOperational(connector);
  const name = connectorName(connector);
  const category = connectorCategory(connector);
  const tone = statusTone(connector);

  return (
    <article
      className={[
        'mission-connector-row',
        selected ? 'mission-connector-row-selected' : '',
        operational ? 'mission-connector-row-connected' : '',
        compact ? 'mission-connector-row-compact' : '',
      ].filter(Boolean).join(' ')}
    >
      <div className="mission-connector-row-identity">
        <div className="mission-connector-row-mark" aria-hidden="true">
          {connector.short || name.slice(0, 2).toUpperCase()}
        </div>
        <div className="mission-connector-row-copy">
          <div className="mission-connector-row-title">
            <h4>{name}</h4>
            <span className="mission-connector-category">{category}</span>
            {recommended ? <span className="mission-connector-recommended">Recommended</span> : null}
          </div>
          <p>{connectorSubtitle(connector)}</p>
          {connector.scope ? <small>{connector.scope}</small> : null}
        </div>
      </div>

      <div className="mission-connector-row-actions">
        {selected ? (
          <span className={`mission-connector-status mission-connector-status-${tone}`}>
            {statusLabel(connector)}
          </span>
        ) : null}

        {!selected ? (
          <button
            type="button"
            className="mission-connector-add-button"
            onClick={() => onToggle?.(connector.key)}
          >
            Add
          </button>
        ) : (
          <>
            <button
              type="button"
              className="mission-connector-connect-button"
              onClick={() => onAuthorize?.(connector.key)}
            >
              {operational ? `Manage ${name}` : `Connect ${name}`}
            </button>
            <button
              type="button"
              className="mission-connector-remove-button"
              onClick={() => onToggle?.(connector.key)}
            >
              Remove
            </button>
          </>
        )}
      </div>
    </article>
  );
}

export default function ConnectorCatalog({
  connectors = [],
  recommendedKeys = [],
  loading = false,
  onToggle,
  onAuthorize,
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState(ALL_CATEGORY);

  const recommendedSet = useMemo(
    () => new Set((recommendedKeys || []).map((key) => normalize(key).replace(/-/g, '_'))),
    [recommendedKeys]
  );

  const sortedConnectors = useMemo(
    () => [...connectors].sort((a, b) => connectorName(a).localeCompare(connectorName(b))),
    [connectors]
  );

  const selectedConnectors = useMemo(
    () => sortedConnectors.filter((connector) => connector.selected === true),
    [sortedConnectors]
  );

  const recommendedConnectors = useMemo(
    () => sortedConnectors.filter((connector) => (
      connector.selected !== true &&
      recommendedSet.has(normalize(connector.key).replace(/-/g, '_'))
    )),
    [recommendedSet, sortedConnectors]
  );

  const categories = useMemo(() => {
    const values = Array.from(new Set(sortedConnectors.map(connectorCategory))).sort();
    return [ALL_CATEGORY, ...values];
  }, [sortedConnectors]);

  const filteredConnectors = useMemo(() => {
    const normalizedQuery = normalize(query);
    return sortedConnectors.filter((connector) => {
      const categoryMatches =
        category === ALL_CATEGORY || connectorCategory(connector) === category;
      const queryMatches =
        !normalizedQuery || connectorSearchText(connector).includes(normalizedQuery);
      return categoryMatches && queryMatches;
    });
  }, [category, query, sortedConnectors]);

  return (
    <div className="mission-connector-catalog">
      <section className="mission-connector-section" aria-labelledby="mission-selected-connectors">
        <div className="mission-connector-section-heading">
          <div>
            <span className="mission-connector-kicker">Your Mission Connectors</span>
            <h4 id="mission-selected-connectors">
              {selectedConnectors.length
                ? `${selectedConnectors.length} system${selectedConnectors.length === 1 ? '' : 's'} selected`
                : 'No systems selected yet'}
            </h4>
          </div>
          <p>Only systems you explicitly add become part of this Mission Launch.</p>
        </div>

        {selectedConnectors.length ? (
          <div className="mission-connector-selected-list">
            {selectedConnectors.map((connector) => (
              <ConnectorRow
                key={connector.key}
                connector={connector}
                recommended={recommendedSet.has(normalize(connector.key).replace(/-/g, '_'))}
                onToggle={onToggle}
                onAuthorize={onAuthorize}
                compact
              />
            ))}
          </div>
        ) : (
          <div className="mission-connector-empty-state">
            <strong>Start with the systems that hold your compliance evidence.</strong>
            <span>Add a recommended connector or search the catalog below.</span>
          </div>
        )}
      </section>

      {recommendedConnectors.length ? (
        <section className="mission-connector-section" aria-labelledby="mission-recommended-connectors">
          <div className="mission-connector-section-heading">
            <div>
              <span className="mission-connector-kicker">Recommended for you</span>
              <h4 id="mission-recommended-connectors">Suggested from your Mission Launch context</h4>
            </div>
            <p>Recommendations are advisory. OASYS never adds a connector without your selection.</p>
          </div>

          <div className="mission-connector-recommended-list">
            {recommendedConnectors.slice(0, 5).map((connector) => (
              <ConnectorRow
                key={connector.key}
                connector={connector}
                recommended
                onToggle={onToggle}
                onAuthorize={onAuthorize}
                compact
              />
            ))}
          </div>
        </section>
      ) : null}

      <section className="mission-connector-section mission-connector-find-section" aria-labelledby="mission-find-connectors">
        <div className="mission-connector-section-heading">
          <div>
            <span className="mission-connector-kicker">Find a connector</span>
            <h4 id="mission-find-connectors">Search the OASYS connector catalog</h4>
          </div>
          <p>The catalog can grow without making Mission Launch an endless wall of cards.</p>
        </div>

        <div className="mission-connector-search-row">
          <label className="mission-connector-search">
            <span className="sr-only">Search connectors</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search connectors..."
            />
          </label>

          <div className="mission-connector-category-filter" aria-label="Connector categories">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                className={category === item ? 'mission-connector-filter-active' : ''}
                aria-pressed={category === item}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mission-connector-empty-state">
            <strong>Refreshing connector catalog…</strong>
            <span>OASYS is checking supported providers and live connection state.</span>
          </div>
        ) : filteredConnectors.length ? (
          <div className="mission-connector-all-list">
            {filteredConnectors.map((connector) => (
              <ConnectorRow
                key={connector.key}
                connector={connector}
                recommended={recommendedSet.has(normalize(connector.key).replace(/-/g, '_'))}
                onToggle={onToggle}
                onAuthorize={onAuthorize}
                compact
              />
            ))}
          </div>
        ) : (
          <div className="mission-connector-empty-state">
            <strong>No connectors match this search.</strong>
            <span>Try another provider name or category.</span>
          </div>
        )}
      </section>
    </div>
  );
}
